import { View, SxProp, styled } from 'dripsy';
import { flatten } from 'lodash';

import React, { useContext, useState } from 'react';

import { TextBody, TextCaption } from '@/design-system/components/typography';
import { keyGenerator } from '@/utils/keyGenerator';
import { LayoutContext, ScreenSize } from 'contexts/LayoutContext';
import { Pressable } from 'react-native';
import { UsePaginationParams } from '../pagination/usePagination.hook';

export const PressableView = styled(Pressable)();

export interface TableItem {
  id: string;
  index: string;
  value: string;
}

export interface TableColumnItem<T> {
  title: string;
  index: keyof T;
  value?: string;
  flex?: number;
  flexVertical?: number;
  wrap?: boolean;
  showTitleOnWrap?: boolean;
  hideColumnTitleOnWrap?: boolean;
  hideOnHorizontal?: boolean;
  hideOnVertical?: boolean;
  textAlign?: 'left' | 'center' | 'right';
  sx?: SxProp;
  titleSx?: SxProp;
  textSx?: SxProp;
  columnSx?: SxProp;
  renderTitle?: (item: T) => JSX.Element;
  renderColumn?: (item: T) => JSX.Element;
  formatValue?: (item: T) => string;
}

export interface TableColumn<T> extends Array<TableColumnItem<T> | Array<TableColumnItem<T>>> {}

export interface UniTableProps<T> {
  data: Array<T>;
  column: TableColumn<T>;
  sx?: SxProp;
  wrapperSx?: SxProp;
  rowSx?: SxProp;
  width?: number;
  itemVariant?: 'default' | 'list';
  overflowScrollEnabled?: boolean;
  pagination?: UsePaginationParams;
  onRowPressed?: (item: T) => void;
}

export const UniTable = <T extends unknown>({
  data,
  column,
  sx,
  wrapperSx,
  rowSx,
  width,
  itemVariant = 'default',
  overflowScrollEnabled,
  onRowPressed
}: UniTableProps<T>) => {
  const layout = useContext(LayoutContext);
  const isVertical = layout.screenSize === ScreenSize.Mobile;

  return (
    <View sx={{ overflowX: overflowScrollEnabled ? 'auto' : 'hidden', ...wrapperSx, padding: '$1' }}>
      <View sx={{ ...styles.container, minWidth: isVertical ? '100%' : width || '100%', ...sx }}>
        {/* Title */}
        <Title<T> data={data} column={column} screenSize={layout.screenSize} itemVariant={itemVariant} />
        {/* ListItem */}
        {data.map((item, i) => {
          const tableItemProps = {
            item,
            column,
            screenSize: layout.screenSize,
            rowSx,
            onRowPressed
          };
          return (
            <React.Fragment key={keyGenerator(`table-item-${i}`)}>
              {itemVariant === 'default' && <TableItem {...tableItemProps} />}
              {itemVariant === 'list' && <TableItemList {...tableItemProps} />}
            </React.Fragment>
          );
        })}
      </View>
    </View>
  );
};

const Title = <T extends unknown>({
  data,
  column,
  screenSize,
  itemVariant
}: {
  data: Array<T>;
  column: TableColumn<T>;
  screenSize: ScreenSize;
  itemVariant: 'default' | 'list';
}) => {
  const isVertical = screenSize === ScreenSize.Mobile;
  let styles = isVertical ? titleStyles.col : titleStyles.row;

  const rawColumnData = isVertical ? [...column] : [flatten(column)];
  const castedColumnData = rawColumnData as Array<Array<TableColumnItem<T>>>;

  if (itemVariant === 'default') {
    styles = { ...styles };
  }

  // const columnSx = { flex: column.flex ? column.flex : 1, paddingX: isVertical ? '$0' : '$3', ...column.sx };
  return (
    <View sx={{ ...styles, pb: '$3' }}>
      {castedColumnData.map((columnRow, i) =>
        columnRow.map((column, i) => {
          if (isVertical && (!column.showTitleOnWrap || column.hideOnVertical)) return null;
          if (!isVertical && column.hideOnHorizontal) return null;

          let columnSx: SxProp = { paddingX: isVertical ? '$0' : '$0' };
          if (column.sx) {
            columnSx = { ...columnSx, ...column.sx };
          } else {
            columnSx = { flex: column.flex ? column.flex : 1, ...columnSx };
          }
          return (
            <View key={keyGenerator(`title-${column.title}`)} {...column} sx={columnSx}>
              {column.renderTitle ? (
                column.renderTitle(data[i])
              ) : (
                <TextCaption secondary sx={{ textAlign: column.textAlign || 'left', paddingX: '$3' }}>
                  {column.title}
                </TextCaption>
              )}
            </View>
          );
        })
      )}
    </View>
  );
};

const TableItem = <T extends unknown>({
  item,
  column,
  screenSize,
  onRowPressed
}: {
  item: T;
  column: TableColumn<T>;
  screenSize: ScreenSize;
  onRowPressed?: (item: T) => void;
}) => {
  const isVertical = screenSize === ScreenSize.Mobile;
  const rawColumnData = isVertical ? [...column] : [flatten(column)];
  const castedColumnData = rawColumnData as Array<Array<TableColumnItem<T>>>;
  const [hovered, setHovered] = useState(false);
  return (
    <PressableView onPress={() => onRowPressed && onRowPressed(item)}>
      {/* {({ hovered }) => { */}
      {/* const backgroundColor = hovered ? '$disable' : '$backgroundPrimary'; */}
      {/* return ( */}
      <View
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          ...styles.tableItem,
          backgroundColor: hovered ? '$disable' : '$backgroundPrimary',
          paddingY: '$6',
          paddingX: '$3',
          borderRadius: 10,
          shadowColor: '#000000',
          shadowOffset: {
            width: 2,
            height: 2
          },
          shadowOpacity: 0.08,
          shadowRadius: 5,
          elevation: 5
        }}>
        {castedColumnData.map((columnRow, i) => (
          <View
            key={keyGenerator(`table-item-${i}`)}
            sx={{
              ...tableItemStyles.row,
              paddingX: isVertical ? '$0' : '$0',
              pt: isVertical ? '$5' : '$0',
              alignItems: 'center'
            }}>
            {columnRow.map((column, i) => {
              if (isVertical && column.wrap) return null;
              let columnSx: SxProp = { paddingX: isVertical ? '$0' : '$0' };
              if (column.sx) {
                columnSx = { ...columnSx, ...column.sx };
              } else {
                columnSx = { flex: column.flex ? column.flex : 1, ...columnSx };
              }
              return (
                <View key={keyGenerator(`table-item-${i}-${column.title}`)} sx={columnSx}>
                  {/* Title */}
                  {isVertical && !column.hideColumnTitleOnWrap && (
                    <TextCaption
                      secondary
                      label
                      sx={{
                        paddingX: isVertical ? '$0' : '$3',
                        textAlign: isVertical ? 'left' : column.textAlign || 'left',
                        ...column.titleSx
                      }}>
                      {column.title}
                    </TextCaption>
                  )}
                  {/* Column */}
                  {column.renderColumn && column.renderColumn(item)}
                  {!column.renderColumn && (
                    <TextBody
                      sx={{
                        paddingX: isVertical ? '$0' : '$3',
                        textAlign: isVertical ? 'left' : column.textAlign || 'left',
                        ...column.columnSx
                      }}>
                      {column.formatValue ? column.formatValue(item) : item[column.index] || ' - '}
                    </TextBody>
                  )}
                </View>
              );
            })}
          </View>
        ))}
      </View>
      {/* ); */}
      {/* }} */}
    </PressableView>
  );
};

const TableItemList = <T extends unknown>({
  item,
  column,
  screenSize,
  rowSx,
  onRowPressed
}: {
  item: T;
  column: TableColumn<T>;
  screenSize: ScreenSize;
  rowSx?: SxProp;
  onRowPressed?: (item: T) => void;
}) => {
  const isVertical = screenSize === ScreenSize.Mobile;
  const rawColumnData = isVertical ? [...column] : [flatten(column)];
  const castedColumnData = rawColumnData as Array<Array<TableColumnItem<T>>>;

  const style = isVertical ? tableItemStyles.col : tableItemStyles.row;

  return (
    <PressableView onPress={() => onRowPressed && onRowPressed(item)}>
      {({ hovered, pressed }) => {
        const backgroundColor = hovered ? '$disable' : 'transparent';
        if (pressed) {
          onRowPressed && onRowPressed(item);
        }

        return (
          <View sx={{ ...styles.tableItem, backgroundColor, paddingY: isVertical ? '$5' : '$6' }}>
            {castedColumnData.map((columnRow, i) => (
              <View key={keyGenerator(`table-item-${i}`)} sx={{ ...style, ...rowSx }}>
                {columnRow.map((column, i) => {
                  if (!isVertical && column.hideOnHorizontal) return null;
                  if (isVertical && (column.hideOnVertical || column.wrap)) return null;
                  const textAlign = isVertical ? 'right' : column.textAlign || 'left';
                  const flex = isVertical ? column.flexVertical || 'auto' : column.flex ? column.flex : 1;
                  return (
                    <View
                      key={keyGenerator(`table-item-${i}-${column.title}`)}
                      sx={{
                        paddingX: isVertical ? '$0' : '$3',
                        flex,
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        ...column.sx
                      }}>
                      {/* Title */}
                      {isVertical && !column.hideColumnTitleOnWrap && (
                        <TextCaption secondary label sx={{ textAlign, ...column.titleSx }}>
                          {column.title}
                        </TextCaption>
                      )}
                      {/* Column */}
                      {column.renderColumn && column.renderColumn(item)}
                      {!column.renderColumn && (
                        <TextBody sx={{ flex: 1, textAlign, ...column.columnSx }}>
                          {column.formatValue ? column.formatValue(item) : item[column.index] || ' - '}
                        </TextBody>
                      )}
                    </View>
                  );
                })}
              </View>
            ))}
          </View>
        );
      }}
    </PressableView>
  );
};

enum StyleName {
  container = 'container',
  title = 'title',
  tableItem = 'tableItem'
}

const styles: Record<StyleName, SxProp> = {
  container: {
    mt: '$10',
    gap: '$4'
  },
  title: {},
  tableItem: {}
};

enum TableItemStyleName {
  row = 'row',
  col = 'col',
  details = 'details'
}

const tableItemStyles: Record<TableItemStyleName, SxProp> = {
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '$3'
  },
  col: {
    gap: '$3'
  },
  details: {
    paddingY: '$3',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  }
};

enum TitleStyleName {
  row = 'row',
  col = 'col'
}

const titleStyles: Record<TitleStyleName, SxProp> = {
  row: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: '$3'
  },
  col: {
    borderBottomWidth: 1,
    borderBottomColor: '$borderPrimary'
  }
};
